import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Download, FileText, Printer } from "lucide-react";
import { istNow, ymd, useIstDay } from "../lib/dates";

const iso = (d) => ymd(d);
const fmtDate = (s) => {
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

function presetRange(preset) {
  const now = istNow();
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (preset === "today") return { from: iso(t), to: iso(t) };
  if (preset === "yesterday") { const y = new Date(t); y.setDate(t.getDate() - 1); return { from: iso(y), to: iso(y) }; }
  if (preset === "week") {
    const day = (t.getDay() + 6) % 7;
    const mon = new Date(t); mon.setDate(t.getDate() - day);
    return { from: iso(mon), to: iso(t) };
  }
  if (preset === "month") {
    const first = new Date(t.getFullYear(), t.getMonth(), 1);
    return { from: iso(first), to: iso(t) };
  }
  return null;
}

export default function StaffIncentiveReport() {
  const { branchParam } = useBranch();
  const [staff, setStaff] = useState([]);
  const initStaff = new URLSearchParams(window.location.search).get("staff") || "";
  const [staffId, setStaffId] = useState(initStaff);
  const [preset, setPreset] = useState(initStaff ? "month" : "today");
  const [custom, setCustom] = useState({ from: iso(istNow()), to: iso(istNow()) });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const istDay = useIstDay();
  const range = useMemo(() => (preset === "custom" ? custom : presetRange(preset)), [preset, custom, istDay]); // istDay: IST midnight rollover

  useEffect(() => {
    api.get("/staff", { params: { branch_id: branchParam } }).then((r) => setStaff(r.data));
  }, [branchParam]);

  useEffect(() => {
    if (!staffId || !range) { setReport(null); return; }
    setLoading(true);
    api.get("/incentives/staff-report", {
      params: { staff_id: staffId, from_date: range.from, to_date: range.to, branch_id: branchParam },
    }).then((r) => setReport(r.data)).catch(() => toast.error("Failed to load report"))
      .finally(() => setLoading(false));
  }, [staffId, range, branchParam]);

  const t = report?.totals;

  const exportCsv = () => {
    if (!report) return;
    const lines = [["Services Count", "Gross", "GST", "Net After GST", "Slab", "Incentive %", "Service Incentive", "Product Sales", "Product Incentive", "Total Incentive"].join(",")];
    lines.push([t.services_count, t.gross_service, t.gst, t.net_service, `"${t.slab_label}"`, t.slab_percent, t.service_incentive, t.product_sales, t.product_incentive, t.total_incentive].join(","));
    lines.push(""); lines.push(["Date", "Bills", "Gross Service", "GST", "Net Service", "Product Sales"].join(","));
    report.rows.forEach((r) => lines.push([r.date, r.bills, r.gross_service, r.gst, r.net_service, r.product_sales].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `incentive_${report.staff_name}_${range.from}_${range.to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (!report) return;
    const token = localStorage.getItem("ds_token");
    const qs = new URLSearchParams({ staff_id: staffId, from_date: range.from, to_date: range.to });
    if (branchParam) qs.append("branch_id", branchParam);
    fetch(`${API}/incentives/staff-report/pdf?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 30000); })
      .catch(() => toast.error("PDF export failed"));
  };

  const cards = [
    { label: "Total Bills", value: t ? t.bills : 0, plain: true },
    { label: "Net Service (after GST)", value: t ? rupee(t.net_service) : rupee(0) },
    { label: "Product Sales", value: t ? rupee(t.product_sales) : rupee(0) },
    { label: "Total Incentive", value: t ? rupee(t.total_incentive) : rupee(0), highlight: true },
  ];

  return (
    <div className="space-y-5" data-testid="staff-incentive-report">
      {/* Controls */}
      <Card className="p-4 bg-card border-border">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px]">
            <label className="text-xs text-muted-foreground">Staff</label>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger className="bg-secondary mt-1" data-testid="report-staff-select"><SelectValue placeholder="Select Staff" /></SelectTrigger>
              <SelectContent>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            {[["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]].map(([k, lbl]) => (
              <Button key={k} size="sm" variant={preset === k ? "default" : "outline"}
                onClick={() => setPreset(k)} data-testid={`report-preset-${k}`}
                className={`rounded-sm ${preset === k ? "bg-primary text-primary-foreground" : ""}`}>{lbl}</Button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-end gap-2">
              <div><label className="text-xs text-muted-foreground">From</label>
                <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} data-testid="report-from" className="bg-secondary mt-1 w-40" /></div>
              <div><label className="text-xs text-muted-foreground">To</label>
                <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} data-testid="report-to" className="bg-secondary mt-1 w-40" /></div>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={exportCsv} disabled={!report} data-testid="report-export-csv"><Download size={14} className="mr-1" /> CSV</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={exportPdf} disabled={!report} data-testid="report-export-pdf"><FileText size={14} className="mr-1" /> PDF</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => window.print()} disabled={!report} data-testid="report-print"><Printer size={14} className="mr-1" /> Print</Button>
          </div>
        </div>
      </Card>

      {!staffId && <Card className="p-10 bg-card border-border text-center text-muted-foreground">Select a staff member to view the incentive report.</Card>}

      {staffId && (
        <div id="incentive-report-print" className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {cards.map((c, i) => (
              <Card key={i} className="p-4 bg-card border-border">
                <p className="text-xs text-muted-foreground">{c.label}</p>
                <p className={`font-display text-3xl mt-1 ${c.highlight ? "text-primary" : ""}`} data-testid={`report-card-${i}`}>{c.plain ? c.value : c.value}</p>
              </Card>
            ))}
          </div>

          {report && (
            <p className="text-sm text-muted-foreground">
              Staff: <span className="text-foreground font-medium">{report.staff_name}</span> · Home Branch: {report.home_branch_name} · {fmtDate(report.from_date)} – {fmtDate(report.to_date)} {new Date(report.to_date).getFullYear()}
            </p>
          )}
          {report?.branch_breakdown?.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="report-branch-breakdown">
              Combined across branches: {report.branch_breakdown.map((b) => `${b.short_name} ${rupee(b.gross_service)}`).join(" · ")} — incentive slab applied on combined Net After GST and allocated to {report.home_branch_name}.
            </p>
          )}

          {/* Incentive calculation (same backend calc as Performance Summary) */}
          {report && t && (
            <Card className="bg-card border-border overflow-x-auto" data-testid="incentive-calc-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-right">Services Count</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">GST</TableHead>
                    <TableHead className="text-right">Net After GST</TableHead><TableHead>Slab</TableHead><TableHead className="text-right">Incentive %</TableHead>
                    <TableHead className="text-right">Service Incentive</TableHead><TableHead className="text-right">Product Sales</TableHead>
                    <TableHead className="text-right">Product Incentive</TableHead><TableHead className="text-right">Total Incentive</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="border-border font-medium" data-testid="incentive-calc-row">
                    <TableCell className="text-right">{t.services_count}</TableCell>
                    <TableCell className="text-right">{rupee(t.gross_service)}</TableCell>
                    <TableCell className="text-right">{rupee(t.gst)}</TableCell>
                    <TableCell className="text-right" data-testid="calc-net">{rupee(t.net_service)}</TableCell>
                    <TableCell>{t.slab_label}</TableCell>
                    <TableCell className="text-right">{t.slab_percent}%</TableCell>
                    <TableCell className="text-right text-primary" data-testid="calc-service-inc">{rupee(t.service_incentive)}</TableCell>
                    <TableCell className="text-right">{rupee(t.product_sales)}</TableCell>
                    <TableCell className="text-right text-primary">{rupee(t.product_incentive)}</TableCell>
                    <TableCell className="text-right text-primary font-semibold" data-testid="report-total-gross">{rupee(t.total_incentive)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          )}

          {/* Day-wise business */}
          <Card className="bg-card border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Date</TableHead><TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Gross Service</TableHead>
                  <TableHead className="text-right">GST</TableHead><TableHead className="text-right">Net Service</TableHead><TableHead className="text-right">Product Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report?.rows.map((r) => (
                  <TableRow key={r.date} className="border-border" data-testid={`report-row-${r.date}`}>
                    <TableCell className="font-medium">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-right">{r.bills}</TableCell>
                    <TableCell className="text-right">{rupee(r.gross_service)}</TableCell>
                    <TableCell className="text-right">{rupee(r.gst)}</TableCell>
                    <TableCell className="text-right">{rupee(r.net_service)}</TableCell>
                    <TableCell className="text-right">{rupee(r.product_sales)}</TableCell>
                  </TableRow>
                ))}
                {report && !report.rows.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No billing in range.</TableCell></TableRow>}
                {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
}
